import { useCallback, useEffect, useState } from "react";

// Sub-pixel rounding can leave a fraction of a pixel unscrolled on a container already at its end.
const SCROLLABLE_THRESHOLD_PX = 1;

/**
 * The container is attached with a callback ref rather than a `useRef` because it renders behind a
 * loader: the node is absent on first render and listeners must attach whenever it mounts.
 */
export const useHorizontalScrollArrow = () => {
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (!scrollContainer) return undefined;

    const updateCanScrollRight = () => {
      const remaining =
        scrollContainer.scrollWidth - scrollContainer.scrollLeft - scrollContainer.clientWidth;
      setCanScrollRight(remaining > SCROLLABLE_THRESHOLD_PX);
    };

    updateCanScrollRight();
    scrollContainer.addEventListener("scroll", updateCanScrollRight);
    const resizeObserver = new ResizeObserver(updateCanScrollRight);
    resizeObserver.observe(scrollContainer);

    return () => {
      scrollContainer.removeEventListener("scroll", updateCanScrollRight);
      resizeObserver.disconnect();
    };
  }, [scrollContainer]);

  const scrollToEnd = useCallback(() => {
    scrollContainer?.scrollTo({ left: scrollContainer.scrollWidth, behavior: "smooth" });
  }, [scrollContainer]);

  return { scrollContainerRef: setScrollContainer, canScrollRight, scrollToEnd };
};
